'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import Section from '@/components/marketing/Section'
import { LinkButton } from '@/components/ui/button'
import JsonLd from '@/components/seo/JsonLd'
import SectionHead from '@/components/marketing/SectionHead'
import Arrow from '@/components/marketing/Arrow'
import CheckList from '@/components/marketing/CheckList'
import {
  getServicePriceModel,
  type ServicePriceKey,
} from '@/lib/services-pricing'
import {
  RADAR_ENTRY_PRICE,
  RADAR_FREE_TIER,
  RADAR_PACK_FEATURES,
  RADAR_PACK_OPTIONS,
  RADAR_RETAINER_TIER,
} from '@/lib/radar/pricing-tiers'

/**
 * /pricing, rebuilt 2026-08-08 from the approved design in
 * public/pixelmojo-refactor/"Pricing — Pixelmojo.html", adapted to the
 * Pixelmojo design system: brand tokens, sharp corners, hairline borders,
 * magenta eyebrows, our button variants.
 *
 * Two things deliberately differ from the mock:
 * - Project-service prices come from getServicePriceModel(), the single
 *   source of truth in services-knowledge.json. The mock hardcodes them;
 *   duplicating a figure a helper already produces is how pricing drifts.
 * - The mock's "Essential Reading + What's New" section is omitted because
 *   the root layout already appends RecentArticles to /pricing. Rendering
 *   the mock's version would duplicate the section AND hardcode article
 *   titles that go stale.
 *
 * The mock's own header, footer, newsletter, and theme toggle are dropped —
 * the global chrome wraps this page.
 *
 * 2026-09-04 (heuristic finding F-01): this is now the ONE page that holds
 * every Pixelmojo price. The header's "Pricing" link used to resolve here
 * while the homepage sold Radar, and Radar's own ladder ($0 / $5 / $199)
 * existed only on /platform#pricing. The Radar section moved from dead last
 * (after the comparison table) to the first priced section on the page and
 * grew from a single "$0, then $5" panel into the full three-tier ladder.
 * Every Radar figure is imported from @/lib/radar/pricing-tiers, which
 * derives them from @/lib/paddle/pricing — the same source /platform reads,
 * so the two pages cannot state different prices.
 *
 * Every price and claim here already appears on the live page (checked
 * against the pre-rebuild file), so nothing new is asserted. The Hive
 * payback line keeps its qualified "targeted inside" wording, which is the
 * form src/lib/banned-claims.test.ts permits.
 */

// The hero path-finder. Each entry maps a buyer intent to the model that
// fits and the figure stated in full further down the page.
const PATHS = [
  {
    key: 'build',
    tab: 'Build a system',
    question: 'An AI product my team owns.',
    model: 'Build + platform',
    price: 'Vector from $35K · Hive from $65K',
  },
  {
    key: 'audit',
    tab: 'Audit visibility',
    question: 'A clear view of our AI visibility.',
    model: 'Free + credits',
    price: `Free first audit · then ${RADAR_ENTRY_PRICE} per audit`,
  },
  {
    key: 'growth',
    tab: 'Grow conversion',
    question: 'Conversion growth with proof first.',
    model: 'Sprint + subscribe',
    price: '$4,500 Sprint · then from $2K/month',
  },
  {
    key: 'project',
    tab: 'Ship a project',
    question: 'One focused deliverable shipped.',
    model: 'Scoped engagement',
    price: 'Fixed scope · from $1,995',
  },
]

const MODEL_CARDS = [
  {
    href: '#ai-products',
    kicker: '01 / Build + platform',
    title: 'An AI product you own',
    copy: 'Vector for lead qualification. Hive for multi-agent teams. You own the code.',
    price: 'From $35K build',
  },
  {
    href: '#radar',
    kicker: '02 / Free + credits',
    title: 'An AI visibility audit',
    copy: '13 tools check what AI says about your brand. One ranked report. No card required.',
    price: `Free first audit, then ${RADAR_ENTRY_PRICE}/audit`,
  },
  {
    href: '#journey',
    kicker: '03 / Sprint + subscribe',
    title: 'Growth services',
    copy: 'Prove value in 60 days. Scale monthly. Performance bonuses align us.',
    price: 'From $4,500 Sprint',
  },
  {
    href: '#projects',
    kicker: '04 / Scoped engagement',
    title: 'A custom project',
    copy: 'Design systems, conversion assets, full-stack AI. Fixed scope, fixed price.',
    price: 'From $1,995',
  },
]

const TIERS = [
  {
    name: 'Starter',
    price: '$2,000',
    unit: 'per month',
    local: 'PH startups: ₱70,000/month',
    desc: 'AI sprint subscription for early-stage startups.',
    features: [
      '1 monthly conversion sprint',
      'Always-on analytics dashboard',
      'Up to 3 A/B tests per month',
      'Quarterly strategy call',
      'Cancel anytime, 30-day notice',
    ],
    cta: 'Get started',
    popular: false,
  },
  {
    name: 'Growth',
    price: '$3,500',
    unit: 'per month',
    local: 'PH: ₱120,000/month',
    desc: 'Full optimization partner for teams scaling past the Sprint.',
    features: [
      'Everything in Starter, plus:',
      'Brand system maintenance',
      'Funnel + campaign management',
      'Dedicated AI/UX manager',
      'Priority support, 72hr turnaround',
    ],
    cta: 'Get started',
    popular: true,
  },
  {
    name: 'Elite',
    price: '$10K–$25K',
    unit: 'per month',
    local: 'PH: ₱350K to ₱850K/month',
    desc: 'Full-stack AI productization for high-growth startups.',
    features: [
      'Unlimited design and dev sprints',
      'Growth consulting + engineering',
      'Executive reporting',
      'Live codebase integration',
      'Custom performance targets',
    ],
    cta: 'Request quote',
    popular: false,
  },
]

const SHARES = [
  {
    value: '5–7%',
    title: 'Of incremental revenue',
    copy: 'Growth services. Sprint graduates on subscription.',
  },
  {
    value: '5%',
    title: 'Of deal value',
    copy: 'Vector. From Vector-qualified leads that close.',
  },
  {
    value: '$2',
    title: 'Per resolution',
    copy: 'Hive. Successful agent interactions only.',
  },
]

const BUILDS = [
  {
    label: 'AI products / 01',
    title: 'Vector / Lead qualification',
    copy: '12-dimension lead qualification for B2B SaaS. Replaces a $75K/year SDR. Break even in year one, save $50K+ annually.',
    price: '$35,000 one-time build',
    addons:
      '+ $1,500/month platform fee · + 5% of deal value from closed deals',
    terms: '60-day build · 12-month minimum · 15% annual prepay discount',
    features: [
      '12-dimension qualification scoring',
      'Real-time conversation intelligence',
      'Automated routing + CRM integration',
      'You own the application code',
    ],
    href: '/vector',
  },
  {
    label: 'AI products / 02',
    title: 'Hive / AI co-workers',
    copy: 'AI co-workers that share context and coordinate autonomously. $2 per resolution against $20 to $50 per human ticket, payback targeted inside 3 to 6 months.',
    price: '$65,000 one-time build',
    addons:
      '+ $3,000/month platform fee · + $2.00 per resolution successful interactions',
    terms: '12-week build · 12-month minimum · 15% annual prepay discount',
    features: [
      '2 to 4 specialized AI co-workers',
      'Shared intelligence layer, includes Vector',
      'Full handoff visibility + context transfer',
      'You own the application code',
    ],
    href: '/hive',
  },
]

const PROJECT_SERVICES: {
  kicker: string
  name: string
  tagline: string
  slug: ServicePriceKey
  href: string
}[] = [
  {
    kicker: '01 / Visibility',
    name: 'AI Visibility Strategy',
    tagline:
      'Get cited by ChatGPT, Claude, Perplexity, and Gemini. GEO and AEO as a sprint plus retainer.',
    slug: 'ai-visibility-strategy',
    href: '/services/ai-visibility-strategy',
  },
  {
    kicker: '02 / Product',
    name: 'AI Product Development',
    tagline:
      'Ship AI products in a 90-day launch window. You own the application code.',
    slug: 'ai-product-development',
    href: '/services/ai-product-development',
  },
  {
    kicker: '03 / Brand',
    name: 'Brand & Sales Design',
    tagline:
      'Brand identity, design systems, pitch decks, and sales collateral shipped as one system.',
    slug: 'revenue-first-design',
    href: '/services/revenue-first-design',
  },
  {
    kicker: '04 / Growth',
    name: 'Growth Marketing',
    tagline:
      'Pipeline generation for B2B SaaS. Content, lead scoring, email automation, and analytics.',
    slug: 'ai-powered-growth',
    href: '/services/ai-powered-growth',
  },
]

const COMPARISON = [
  {
    feature: 'Commitment',
    them: '6 to 12 months',
    us: '60 days, then month-to-month',
  },
  {
    feature: 'Time to results',
    them: '2 to 3 months',
    us: '14 days, first sprint',
  },
  {
    feature: 'Monthly cost',
    them: '$8K to $15K',
    us: '$2K to $3.5K after Sprint',
  },
  {
    feature: 'Performance incentive',
    them: 'None',
    us: 'Quarterly bonuses, shared upside',
  },
  {
    feature: 'Speed',
    them: '4 to 8 weeks per project',
    us: '2-week sprints',
  },
  {
    feature: 'Risk',
    them: 'Pay regardless of results',
    us: 'Prove value first with the Sprint',
  },
]

const FAQS = [
  {
    q: 'Which pricing model is right for me?',
    a: 'If you need an AI system built (lead qualification, multi-agent support), look at Vector or Hive. If you need growth marketing and conversion optimization, start with the 60-Day Sprint. If you just want to know what AI is saying about your brand, run a free Radar audit. If you have a specific deliverable in mind (deck, landing page, design system), contact us about project pricing.',
  },
  {
    q: 'Can I start with Radar and upgrade later?',
    a: "Yes. Many clients start with a free Radar audit to understand their AI visibility, then move to a Sprint for conversion work, then to Vector or Hive once they're ready for production AI. There's no required sequence, but Radar is the lowest-commitment way to see what we do.",
  },
  {
    q: 'Do I own the code with Vector and Hive?',
    a: 'Yes. You own the application code. We maintain the intelligence platform (LLM costs, model training, infrastructure). If you stop paying the platform fee, you keep all the code we built. The AI features that depend on our platform would stop working, but the application itself is yours.',
  },
  {
    q: 'Why pick subscription vs. retainer vs. one-time project?',
    a: 'RETAINER (Traditional): 6-12 month commitment, $8K-15K/month regardless of results. Best for large enterprises. ONE-TIME PROJECT: Fixed scope, fixed price, no ongoing relationship. Best for single deliverable (logo, website). SPRINT + SUBSCRIPTION (Us): Prove value in 60 days, then scale with monthly subscription ($2K-$3.5K). Performance bonuses align our success with yours. Best for startups/scale-ups who need continuous growth.',
  },
  {
    q: 'Why is the Sprint cheaper than your subscription?',
    a: "It's not. It's our way of proving ourselves before you commit long-term. Most Sprint clients convert to subscriptions because the results speak for themselves. The Sprint is designed to de-risk the decision.",
  },
  {
    q: "What if you don't deliver results?",
    a: "We measure everything. If we don't hit conversion targets, we provide a detailed analysis of why and what needs to change (often it's product-market fit, not design). You'll know exactly what to fix. No results? No performance bonus.",
  },
  {
    q: 'How is this different from typical agencies?',
    a: 'Three ways: (1) Speed - AI-native workflows move faster than traditional agency cycles. (2) Measurement - Data-driven, not creative intuition. (3) Alignment - Performance bonuses mean we profit when you profit.',
  },
  {
    q: 'Why the price difference between PH and global clients?',
    a: 'We price based on local market purchasing power. Philippine startups get the same quality work at rates that make sense for the local market.',
  },
  {
    q: 'Can I cancel the subscription anytime?',
    a: "Yes! After completing the 60-day Sprint, subscriptions are month-to-month with 30-day cancellation notice. No long-term lock-ins. If results don't match projections, we'll work with you to understand why.",
  },
  {
    q: 'Can I negotiate the pricing?',
    a: "For funded startups (YC/Antler/500 Startups portfolio), we're open to discussion. For bootstrapped founders, we can structure payment plans.",
  },
]

export default function PricingPage() {
  const [path, setPath] = useState(2) // "Grow conversion" leads, as in the mock
  const [openFaq, setOpenFaq] = useState<number | null>(0)
  const userPicked = useRef(false)

  // The finder cycles on its own until the visitor picks a path, then it
  // stops and stays on their choice. Motion-gated: a reduced-motion
  // preference never starts the timer.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const id = setInterval(() => {
      if (userPicked.current) return
      setPath(p => (p + 1) % PATHS.length)
    }, 4300)
    return () => clearInterval(id)
  }, [])

  const pricingFaqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Which pricing model is right for me?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'If you need an AI system built (lead qualification, multi-agent support), look at Vector or Hive. If you need growth marketing and conversion optimization, start with the 60-Day Sprint. If you just want to know what AI is saying about your brand, run a free Radar audit. If you have a specific deliverable in mind (deck, landing page, design system), contact us about project pricing.',
        },
      },
      {
        '@type': 'Question',
        name: 'Do I own the code with Vector and Hive?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. You own the application code. We maintain the intelligence platform (LLM costs, model training, infrastructure). If you stop paying the platform fee, you keep all the code we built.',
        },
      },
      {
        '@type': 'Question',
        name: 'How much does Pixelmojo cost?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Four pricing models: Vector ($35K build + $1,500/month + 5% of deal value), Hive ($65K build + $3,000/month + $2/resolution), Growth Sprint ($4,500 one-time), and Radar (free first audit, then audit packs from ${RADAR_ENTRY_PRICE} or Pro Retainer ${RADAR_RETAINER_TIER.price}/mo). Growth subscriptions start at $2,000/month after the Sprint. Fixed-scope project services range from $1,995 (Brand & Sales Design) to $49,995 (AI Product Development), with AI Visibility Strategy from $4,500 and Growth Marketing retainers from $2,995/month.`,
        },
      },
    ],
  }

  const active = PATHS[path]

  return (
    <>
      <JsonLd data={pricingFaqJsonLd} />

      {/* ── Hero: statement left, path finder right */}
      <Section width='wide' id='top'>
        <div className='grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center'>
          <div>
            <span className='block instrument-label mb-[18px]'>
              Pricing / Four models
            </span>
            <h1 className='thesis-headline thesis-headline--category'>
              <span className='block'>Four ways to work.</span>
              <span className='block text-cta'>Here is how to pick yours.</span>
            </h1>
            <p className='text-lg text-muted-foreground leading-relaxed max-w-xl mt-6'>
              AI products with fixed builds. Growth services with performance
              bonuses. Visibility audits you can run yourself. Different
              problems, different pricing.
            </p>
            <div className='flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4 mt-8'>
              <LinkButton
                href='/contact-us'
                variant='action'
                size='lg'
                className='group'
              >
                Book Discovery Call
                <Arrow />
              </LinkButton>
              <LinkButton
                href='/platform'
                variant='neutral'
                size='lg'
                className='group'
              >
                Start free with Radar
                <Arrow />
              </LinkButton>
            </div>
          </div>

          <aside
            className='border border-line-strong bg-card'
            aria-label='Pricing path selector'
          >
            <div className='flex items-center justify-between gap-4 px-5 py-3 border-b border-line'>
              <span className='instrument-label text-muted-foreground'>
                Find your model
              </span>
              <span className='instrument-label text-growth'>4 paths open</span>
            </div>
            <div className='p-5'>
              <span className='instrument-label text-muted-foreground'>
                What do you need right now?
              </span>
              <div className='card-headline mt-2'>{active.question}</div>

              <div
                className='grid grid-cols-2 gap-2 mt-5'
                role='tablist'
                aria-label='Pricing models'
              >
                {PATHS.map((p, i) => (
                  <button
                    key={p.key}
                    type='button'
                    role='tab'
                    aria-selected={i === path}
                    onClick={() => {
                      userPicked.current = true
                      setPath(i)
                    }}
                    className={`flex items-center gap-2.5 border px-3 py-2.5 text-left transition-colors ${
                      i === path
                        ? 'border-cta bg-cta/10 text-foreground'
                        : 'border-line-strong text-muted-foreground hover:border-border hover:text-foreground'
                    }`}
                  >
                    <span className='instrument-label'>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className='text-xs font-semibold'>{p.tab}</span>
                  </button>
                ))}
              </div>

              <div className='mt-5 pt-5 border-t border-line'>
                <span className='instrument-label text-muted-foreground'>
                  Best-fit model
                </span>
                <div className='card-headline card-headline--xl mt-1.5'>
                  {active.model}
                </div>
                <p className='text-sm text-muted-foreground m-0 mt-1'>
                  {active.price}
                </p>
              </div>

              <div
                className='h-1 bg-muted/60 mt-5 overflow-hidden'
                aria-hidden='true'
              >
                <div
                  className='h-full bg-[var(--cta-fill)] transition-[width] duration-500'
                  style={{ width: `${((path + 1) / PATHS.length) * 100}%` }}
                />
              </div>
            </div>
          </aside>
        </div>
      </Section>

      {/* ── Four models. The mock's boxed board: one framed grid with 1px
          hairline gaps, magenta mono kickers, a deliberate gap under the
          kicker, and the price pinned to the bottom of every cell so the
          four read as one row. Hover lifts the cell out of the frame. */}
      <Section width='wide'>
        <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line-strong'>
          {MODEL_CARDS.map(card => (
            <Link
              key={card.kicker}
              href={card.href}
              className='group relative bg-card p-6 min-h-[245px] flex flex-col transition-[background-color,transform] duration-200 hover:bg-muted/40 hover:-translate-y-1 hover:z-10'
            >
              <span className='instrument-label'>{card.kicker}</span>
              {/* .model-card h3 { margin: 42px 0 12px }. The spacing sits on
                  this wrapper because .card-headline sets `margin: 0` and
                  would discard margin utilities on the heading itself. */}
              <div className='pt-[42px]'>
                <h3 className='card-headline'>{card.title}</h3>
                <p className='text-xs text-muted-foreground leading-relaxed m-0 mt-3'>
                  {card.copy}
                </p>
              </div>
              {/* .model-card strong { margin-top: auto } — no extra padding,
                  so the price sits directly under the copy on a 245px card. */}
              <span className='flex items-center gap-2 card-headline card-headline--base mt-auto'>
                {card.price}
                <Arrow />
              </span>
            </Link>
          ))}
        </div>
      </Section>

      {/* ── Radar: the self-serve ladder.

          Moved here from the bottom of the page (F-01). It is the FIRST
          priced section a visitor meets because the homepage sells Radar,
          and a visitor primed by "no credit card, access in 30 seconds"
          who clicks Pricing has to land on the $0 rung, not a $65,000
          engagement. Services still follow, in ascending order.

          Every figure comes from @/lib/radar/pricing-tiers (derived from
          the Paddle constants) — the same import /platform reads, so this
          page can never quote a Radar price the product does not charge.
          The `.price-card` spec is the one already used on /platform, so
          the two ladders read as the same object.

          Checkout itself stays on /platform, where the free-check gate,
          email verification and the Paddle overlay live. These CTAs link
          there rather than opening a second checkout surface. */}
      <Section width='wide' id='radar'>
        <SectionHead
          eyebrow='Self-serve / Radar'
          title='Radar: audit your AI visibility yourself.'
          lead='Not ready for a sprint? 13 tools, one report, one ranked fix list with AI-ready implementation prompts.'
        />
        <div className='grid md:grid-cols-3 gap-5 items-stretch'>
          <div className='price-card'>
            <p className='price-plan'>{RADAR_FREE_TIER.plan}</p>
            <p className='price-amount'>
              <strong>{RADAR_FREE_TIER.price}</strong>
              <span>{RADAR_FREE_TIER.unit}</span>
            </p>
            <p className='price-note'>{RADAR_FREE_TIER.note}</p>
            <LinkButton
              href='/platform#free-audit'
              variant='neutral'
              className='price-cta group'
            >
              Get My Free Snapshot
              <Arrow />
            </LinkButton>
            <ul className='price-features'>
              {RADAR_FREE_TIER.features.map(f => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </div>

          <div className='price-card price-card--featured'>
            <p className='price-plan'>Audit packs</p>
            <p className='price-amount'>
              <strong>{RADAR_PACK_OPTIONS[0].price}</strong>
              <span>{RADAR_PACK_OPTIONS[0].unit}</span>
            </p>
            <p className='price-note'>{RADAR_PACK_OPTIONS[0].desc}</p>
            {/* The rest of the ladder, stated rather than made interactive:
                the pack selector itself stays on /platform, where the
                checkout it drives lives. */}
            <p className='text-xs text-muted-foreground m-0 mt-3'>
              {RADAR_PACK_OPTIONS.slice(1)
                .map(opt => `${opt.label} ${opt.price}`)
                .join(' · ')}
              {' · '}
              {RADAR_PACK_OPTIONS[RADAR_PACK_OPTIONS.length - 1].perAudit} per
              audit
            </p>
            <LinkButton
              href='/platform#pricing'
              variant='action'
              className='price-cta group'
            >
              Choose audit pack
              <Arrow />
            </LinkButton>
            <ul className='price-features'>
              {RADAR_PACK_FEATURES.map(f => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </div>

          <div className='price-card'>
            <p className='price-plan'>{RADAR_RETAINER_TIER.plan}</p>
            <p className='price-amount'>
              <strong>{RADAR_RETAINER_TIER.price}</strong>
              <span>{RADAR_RETAINER_TIER.unit}</span>
            </p>
            <p className='price-note'>{RADAR_RETAINER_TIER.note}</p>
            <LinkButton
              href='/platform#pricing'
              variant='neutral'
              className='price-cta group'
            >
              Start Pro
              <Arrow />
            </LinkButton>
            <ul className='price-features'>
              {RADAR_RETAINER_TIER.features.map(f => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </div>
        </div>
        <p className='text-sm text-muted-foreground m-0 mt-8'>
          No card required to start. Audit credits never expire.
        </p>
      </Section>

      {/* ── The journey */}
      <Section width='wide' id='journey'>
        <SectionHead
          eyebrow='The journey'
          title='Sprint first, subscribe after.'
          lead='Traditional retainers ask for 6 to 12 months before proving value. This model flips it: prove ROI in 60 days, then decide if a subscription makes sense. No pressure, no lock-in.'
        />
        <div className='grid md:grid-cols-2 gap-3'>
          <article className='min-h-[465px] flex flex-col p-8 border border-line-strong bg-card'>
            <div className='flex justify-between gap-5 instrument-label'>
              <span>Step 1</span>
              <span>Required</span>
            </div>
            <h3 className='card-headline card-headline--4xl mt-14'>
              60-Day Conversion Sprint
            </h3>
            <p className='text-sm text-muted-foreground leading-relaxed m-0 mt-3'>
              Prove value before you commit long-term.
            </p>
            <div className='card-headline card-headline--4xl mt-7 mb-1.5'>
              $4,500
            </div>
            <span className='text-xs text-muted-foreground'>
              Global / US &nbsp;·&nbsp; ₱180,000 PH startups
            </span>
            <CheckList
              items={[
                'Analytics audit + conversion baseline',
                'AI-optimized landing page or funnel',
                '3 to 5 A/B tests with real traffic',
                '60-day performance report',
              ]}
              className='card-footer'
            />
          </article>

          <article className='min-h-[465px] flex flex-col p-8 border border-line-strong bg-muted/30'>
            <div className='flex justify-between gap-5 instrument-label'>
              <span>Step 2</span>
              <span>Optional</span>
            </div>
            <h3 className='card-headline card-headline--4xl mt-14'>
              AI Growth Subscription
            </h3>
            <p className='text-sm text-muted-foreground leading-relaxed m-0 mt-3'>
              For Sprint graduates who want continuous growth.
            </p>
            <div className='mt-7'>
              {[
                { name: 'Starter', price: '$2,000/month', popular: false },
                { name: 'Growth', price: '$3,500/month', popular: true },
                { name: 'Elite', price: '$10K to $25K/month', popular: false },
              ].map(t => (
                <div
                  key={t.name}
                  className='grid grid-cols-[1fr_auto] gap-5 py-3.5 border-t border-line text-xs'
                >
                  <strong className='card-headline card-headline--base'>
                    {t.name}
                    {t.popular && (
                      <span className='ml-2 instrument-label'>
                        Most popular
                      </span>
                    )}
                  </strong>
                  <span className='text-muted-foreground'>{t.price}</span>
                </div>
              ))}
            </div>
            <CheckList
              items={[
                'Month-to-month, cancel anytime',
                'Performance bonuses included',
                'Live analytics dashboard',
              ]}
              className='card-footer'
            />
          </article>
        </div>
      </Section>

      {/* ── Subscription tiers */}
      <div className='border-t border-line'>
        <Section width='wide' id='subscriptions'>
          <SectionHead
            eyebrow='AI growth subscription'
            title='Pick the tier that matches your stage.'
            lead='All tiers include performance bonuses. Cancel anytime with 30-day notice.'
          />
          {/* .tier-card: 570px min-height, 30px padding, 55px gap above the
              price, checks pinned to the bottom. The popular tier is a fully
              pink card with a white corner banner, per the mock. */}
          <div className='grid md:grid-cols-3 gap-3'>
            {TIERS.map(tier => (
              <article
                key={tier.name}
                className={`relative min-h-[570px] flex flex-col p-[30px] border ${
                  tier.popular
                    ? 'bg-[var(--cta-fill)] border-[var(--cta-fill)] text-white'
                    : 'bg-card border-line-strong'
                }`}
              >
                {tier.popular && (
                  <span className='absolute top-0 right-0 bg-white text-[var(--cta-fill)] instrument-label px-3 py-2'>
                    Most popular
                  </span>
                )}
                <span
                  className={`instrument-label ${
                    tier.popular ? 'text-white' : 'text-cta'
                  }`}
                >
                  {tier.name}
                </span>
                <div className='flex items-baseline gap-2 mt-[55px] mb-1.5'>
                  <span className='card-headline card-headline--4xl'>
                    {tier.price}
                  </span>
                  <span
                    className={`text-sm tracking-[-0.02em] ${
                      tier.popular ? 'text-white/80' : 'text-muted-foreground'
                    }`}
                  >
                    {tier.unit}
                  </span>
                </div>
                <span
                  className={`text-xs ${
                    tier.popular ? 'text-white/75' : 'text-muted-foreground'
                  }`}
                >
                  {tier.local}
                </span>
                <p
                  className={`text-sm leading-relaxed m-0 mt-6 min-h-[66px] ${
                    tier.popular ? 'text-white/85' : 'text-muted-foreground'
                  }`}
                >
                  {tier.desc}
                </p>
                <LinkButton
                  href='/contact-us'
                  variant={tier.popular ? 'neutral' : 'action'}
                  className={`w-full mt-6 group ${
                    tier.popular
                      ? '!border-white/70 !text-white hover:!bg-white hover:!text-[var(--cta-fill)]'
                      : ''
                  }`}
                >
                  {tier.cta}
                  <Arrow />
                </LinkButton>
                <CheckList items={tier.features} className='card-footer' />
              </article>
            ))}
          </div>
        </Section>
      </div>

      {/* ── Performance share. A full-bleed magenta band, per the mock: the
          one place on the page that inverts, so the alignment argument reads
          as a statement rather than another card row. White-on-magenta uses
          --cta-fill (the contrast-tuned fill), and the section head is
          inlined here because SectionHead's magenta eyebrow and muted lead
          would both disappear against this ground. */}
      <div className='bg-[var(--cta-fill)] text-white'>
        <Section width='wide' id='performance'>
          <div className='flex flex-col md:flex-row md:items-end md:justify-between gap-8 md:gap-12'>
            <div>
              <span className='instrument-label text-white/70'>
                Performance share
              </span>
              <h2 className='thesis-headline mt-5'>
                A share of the value created. Nothing else.
              </h2>
            </div>
            <p className='text-white/80 leading-relaxed max-w-lg m-0'>
              No results, no performance share. The model only pays out when you
              grow.
            </p>
          </div>

          <div className='grid sm:grid-cols-3 border border-white/35 mt-12'>
            {/* Mock geometry, exactly: 260px min-height, 30px padding, and a
                70px gap under the numeral (45px below the sm breakpoint,
                where the mock tightens it). */}
            {SHARES.map((s, i) => (
              <article
                key={s.title}
                className={`p-[30px] min-h-[260px] flex flex-col ${
                  i < SHARES.length - 1
                    ? 'border-b sm:border-b-0 sm:border-r border-white/30'
                    : ''
                }`}
              >
                <div className='card-headline card-headline--4xl leading-[0.8] mb-[45px] sm:mb-[70px]'>
                  {s.value}
                </div>
                <h3 className='card-headline'>{s.title}</h3>
                <p className='text-xs text-white/72 leading-relaxed m-0 mt-3'>
                  {s.copy}
                </p>
              </article>
            ))}
          </div>
        </Section>
      </div>

      {/* ── AI products */}
      <div className='border-t border-line'>
        <Section width='wide' id='ai-products'>
          <SectionHead
            eyebrow='AI products'
            title='Ready for a complete AI system?'
            lead='Fixed-scope builds. You own the application code. The intelligence platform, LLM costs, model training, and support are covered by the platform fee.'
          />
          <div className='grid md:grid-cols-2 gap-3'>
            {BUILDS.map(b => (
              <article
                key={b.title}
                className='min-h-[590px] flex flex-col p-8 border border-line-strong bg-card'
              >
                <span className='instrument-label'>{b.label}</span>
                <h3 className='card-headline card-headline--4xl mt-14'>
                  {b.title}
                </h3>
                <p className='text-sm text-muted-foreground leading-relaxed m-0 mt-3'>
                  {b.copy}
                </p>
                <div className='mt-7 py-[18px] border-y border-line'>
                  <strong className='block card-headline card-headline--3xl'>
                    {b.price}
                  </strong>
                  <span className='block text-xs text-muted-foreground mt-2'>
                    {b.addons}
                  </span>
                </div>
                <p className='my-4 instrument-label normal-case tracking-normal leading-[1.6] text-muted-foreground m-0'>
                  {b.terms}
                </p>
                <CheckList items={b.features} className='card-footer' />
                <Link
                  href={b.href}
                  className='group inline-flex items-center gap-2.5 text-sm font-bold text-foreground hover:text-cta transition-colors mt-auto pt-6'
                >
                  Learn more
                  <Arrow />
                </Link>
              </article>
            ))}
          </div>
          <p className='mt-6 py-[18px] px-[22px] border-l-[3px] border-cta bg-muted/30 text-xs text-muted-foreground leading-relaxed m-0'>
            Payment plans available for funded startups. YC, Antler, and 500
            Startups portfolio companies, ask about flexible terms.
          </p>
        </Section>
      </div>

      {/* ── Project services. Prices come from services-knowledge.json via
          getServicePriceModel so this page can never quote a stale figure. */}
      <Section width='wide' id='projects'>
        <SectionHead
          eyebrow='Project services'
          title='Prefer a fixed-scope project?'
          lead='Focused services that ship on a fixed scope. You own everything built.'
        />
        <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line-strong'>
          {PROJECT_SERVICES.map(svc => {
            const model = getServicePriceModel(svc.slug, svc.href)
            return (
              <Link
                key={svc.slug}
                href={svc.href}
                className='group min-h-[325px] flex flex-col p-[25px] bg-card transition-colors hover:bg-muted/40'
              >
                {/* Mock's geometry (325px card, 25px padding, 45px above the
                    title, price pinned by margin-top:auto) with OUR type
                    classes — instrument-label, card-headline, card-copy. */}
                <span className='instrument-label'>{svc.kicker}</span>
                <h3 className='card-headline mt-[45px] mb-3'>{svc.name}</h3>
                <p className='card-copy text-muted-foreground leading-[1.65] m-0'>
                  {svc.tagline}
                </p>
                {model && (
                  <strong className='flex items-center gap-2 card-headline card-headline--base mt-auto'>
                    {model.heroPrice}
                    <Arrow />
                  </strong>
                )}
              </Link>
            )
          })}
        </div>
      </Section>

      {/* ── Comparison */}
      <div className='border-t border-line'>
        <Section width='wide' id='comparison'>
          <SectionHead
            eyebrow='The comparison'
            title='Traditional agencies vs this model.'
          />
          <div className='overflow-x-auto'>
            <div className='min-w-[42rem] border border-line-strong bg-card'>
              <div className='grid grid-cols-[0.7fr_1fr_1fr] border-b border-line bg-muted/40 instrument-label text-muted-foreground'>
                <div className='px-6 py-[21px] border-r border-line'>
                  Feature
                </div>
                <div className='px-6 py-[21px] border-r border-line'>
                  Traditional agencies
                </div>
                <div className='px-6 py-[21px]'>Pixelmojo</div>
              </div>
              {COMPARISON.map((row, i) => (
                <div
                  key={row.feature}
                  className={`grid grid-cols-[0.7fr_1fr_1fr] text-sm ${
                    i < COMPARISON.length - 1 ? 'border-b border-line' : ''
                  }`}
                >
                  <div className='px-6 py-[21px] border-r border-line instrument-label text-muted-foreground'>
                    {row.feature}
                  </div>
                  <div className='px-6 py-[21px] border-r border-line text-muted-foreground'>
                    {row.them}
                  </div>
                  <div className='px-6 py-[21px] font-bold text-foreground'>
                    {row.us}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </div>

      {/* ── FAQ */}
      <div className='border-t border-line'>
        <Section width='wide' id='faq'>
          <SectionHead
            eyebrow='Questions'
            title='Frequently asked.'
            lead='The practical details behind each pricing model.'
          />
          {/* .faq-list / .faq-item / .faq-question / .faq-icon, exactly:
              a 55px number column (38px, then 30px on small screens), 25px
              row padding, the question at clamp(1.15rem,2vw,1.55rem), and a
              30px boxed "+" that rotates 45deg and fills pink when open. */}
          <div className='border-t border-line-strong'>
            {FAQS.map((faq, i) => {
              const open = openFaq === i
              return (
                <div key={faq.q} className='border-b border-line'>
                  <button
                    type='button'
                    aria-expanded={open}
                    onClick={() => setOpenFaq(open ? null : i)}
                    className='w-full grid grid-cols-[30px_1fr_auto] sm:grid-cols-[38px_1fr_auto] md:grid-cols-[55px_1fr_auto] gap-3 md:gap-5 items-center py-[25px] text-left'
                  >
                    <span className='instrument-label text-cta'>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <strong className='card-headline card-headline--xl'>
                      {faq.q}
                    </strong>
                    <span
                      className={`w-[30px] h-[30px] grid place-items-center border font-mono transition-transform duration-[250ms] ${
                        open
                          ? 'rotate-45 bg-[var(--cta-fill)] border-[var(--cta-fill)] text-white'
                          : 'border-line text-foreground'
                      }`}
                      aria-hidden='true'
                    >
                      +
                    </span>
                  </button>
                  {open && (
                    <p className='max-w-[850px] ml-[50px] md:ml-[75px] mr-[10px] md:mr-[50px] mb-[25px] text-sm leading-[1.75] text-muted-foreground m-0'>
                      {faq.a}
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        </Section>
      </div>
    </>
  )
}
